import { Box, Stack, Tooltip } from '@mui/material';
import { DemandTopicStatus, FilterParams } from '@synergy/models';
import { FormattedMessage, useIntl } from 'react-intl';
import { Localized, UrlFilter } from '@synergy/components';
import {
    PaloColumn,
    PaloFilterChips,
    PaloFilterModal,
    PaloListView,
    PaloRowAction,
    PaloStatusPill,
} from '@synergy/components-palo';
import { ReactElement, useEffect, useState } from 'react';
import { getDemandTopicStatusTooltipTranslationId, getDemandTopicStatusTranslationId } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';
import { useSearchParams } from 'react-router-dom';

import * as DemandActions from '../store/demands-topics-actions';
import { Contact } from '../../../../common/models/contact';
import { DemandTopicStatusFilter } from '../../../../common/components/filter/demand-topic-status-filter';
import { DemandTopicsListItem } from '../models/demand-topics-list-item';
import { DemandsTopicContactAssignModal } from '../components/demands-topics-contact-assign-modal';
import { activeLoginsSelector } from '../../../../common/selectors/contacts-logins-selector';
import { fetchUsers } from '../../../../common/actions/contacts-logins-actions';
import { useSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const c = tokens.color;

/** As the legacy filter, without its search field — the list header owns that. */
const PaloDemandsTopicsFilter = (): ReactElement => (
    <PaloFilterModal>
        <DemandTopicStatusFilter />
    </PaloFilterModal>
);

export const PaloDemandsTopics = (): ReactElement => {
    const dispatch = useThunkDispatch();
    const intl = useIntl();
    const [searchParams, setSearchParams] = useSearchParams();
    const data = useSelector(state => state.demands.topics.data);
    const availableContacts = useSelector(activeLoginsSelector);
    const [assignModalData, setAssignModalData] = useState<DemandTopicsListItem | null>(null);

    const page = Number(searchParams.get('page') ?? 1); // 1-based
    const limit = Number(searchParams.get('limit') ?? 50);

    useEffect(() => {
        dispatch(DemandActions.fetchDemandTopics(searchParams));

        return () => {
            dispatch(DemandActions.resetDemandOverview());
        };
    }, [dispatch, searchParams]);

    useEffect(() => {
        // Everyone who could be put on a topic, for the assign dialog.
        dispatch(fetchUsers(new URLSearchParams({ [FilterParams.PageSize]: '10000' })));
    }, [dispatch]);

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const confirmAssign = (contacts: Contact[]): void => {
        if (!assignModalData) {
            return;
        }

        dispatch(DemandActions.assignContacts(assignModalData.id, contacts));
        setAssignModalData(null);
    };

    const columns: PaloColumn<DemandTopicsListItem>[] = [
        {
            key: 'status',
            type: 'status',
            head: <FormattedMessage id="app.table.column.status" />,
            cell: topic => (
                <Tooltip
                    placement="top"
                    title={<FormattedMessage id={getDemandTopicStatusTooltipTranslationId(topic.status)} />}
                >
                    <Box sx={{ display: 'inline-flex' }}>
                        <PaloStatusPill
                            status={topic.status}
                            label={<FormattedMessage id={getDemandTopicStatusTranslationId(topic.status)} />}
                        />
                    </Box>
                </Tooltip>
            ),
        },
        {
            key: 'topic',
            type: 'name',
            head: <FormattedMessage id="app.table.column.topic" />,
            wrap: true,
            cell: topic => (
                <Stack>
                    <Box>
                        <Localized value={topic.title} />
                    </Box>
                    <Box sx={{ fontSize: '11.5px', color: c.ink3 }}>
                        <Localized value={topic.description} />
                    </Box>
                </Stack>
            ),
        },
        {
            key: 'assignedContacts',
            type: 'name',
            head: <FormattedMessage id="app.table.column.assignedContact" />,
            wrap: true,
            cell: topic => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {topic.contacts?.map(contact => (
                        <Box
                            key={contact.id}
                            sx={{
                                fontSize: '11px',
                                backgroundColor: c.neutralSoft,
                                color: c.neutralInk,
                                borderRadius: '9px',
                                padding: '1px 8px',
                            }}
                        >
                            {contact.fullName}
                        </Box>
                    ))}
                </Box>
            ),
        },
    ];

    const rowActions = (topic: DemandTopicsListItem): PaloRowAction[] => [
        {
            label: <FormattedMessage id="app.demands.topics.assignContact" />,
            onClick: () => setAssignModalData(topic),
        },
        // Only a topic nobody has taken on can be declined, as in the old menu.
        ...(DemandTopicStatus.Unassigned === topic.status
            ? [
                  {
                      label: <FormattedMessage id="app.demands.topics.notInterested" />,
                      onClick: () => dispatch(DemandActions.noInterest(topic.id)),
                  },
              ]
            : []),
    ];

    return (
        <>
            <UrlFilter>
                <PaloListView<DemandTopicsListItem>
                    title={<FormattedMessage id="app.navigation.demands.topics" />}
                    filters={<PaloDemandsTopicsFilter />}
                    filterChips={<PaloFilterChips />}
                    searchValue={searchParams.get('search') ?? ''}
                    searchPlaceholder={intl.formatMessage({ id: 'app.common.searchPlaceholder' })}
                    onSearchChange={value => {
                        const next = new URLSearchParams(searchParams);
                        if (value) {
                            next.set('search', value);
                        } else {
                            next.delete('search');
                        }
                        next.set('page', '1');
                        setSearchParams(next, { replace: true });
                    }}
                    columns={columns}
                    items={data.items}
                    getRowId={topic => topic.id}
                    emptyLabel={<FormattedMessage id="app.common.emptyResult" />}
                    rowActions={rowActions}
                    pagination={{
                        count: data.total,
                        page,
                        limit,
                        onPageChange: newPage => updateParams({ page: String(newPage) }),
                        onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
                    }}
                />
            </UrlFilter>

            <DemandsTopicContactAssignModal
                availableContacts={availableContacts}
                contacts={assignModalData?.contacts ?? []}
                isVisible={!!assignModalData}
                onCancel={() => setAssignModalData(null)}
                onConfirm={confirmAssign}
            />
        </>
    );
};
